import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, Target, TrendingUp, BarChart3, Award,
  ChevronRight, AlertTriangle, CheckCircle, Loader2
} from "lucide-react";

const CATEGORY_LABELS = {
  general: "General",
  obc: "OBC",
  sc: "SC",
  st: "ST",
  ews: "EWS",
  pwd: "PwD",
};

const BankingCutoffTrackerPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [cutoffs, setCutoffs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [compLoading, setCompLoading] = useState(false);

  useEffect(() => { loadCutoffs(); }, []);

  const loadCutoffs = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/cutoffs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCutoffs(data.cutoffs || {});
    } catch { toast({ variant: "destructive", description: "Failed to load cutoffs" }); }
    finally { setLoading(false); }
  };

  const loadComparison = async (examType) => {
    setSelectedExam(examType);
    setCompLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/cutoff-comparison?exam_type=${examType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setComparison(data);
    } catch { toast({ variant: "destructive", description: "Failed to load comparison" }); }
    finally { setCompLoading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/banking")} data-testid="cutoff-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Cutoff Tracker</h1>
            <p className="text-sm text-slate-500">Expected cutoffs & your score comparison</p>
          </div>
        </div>

        {/* Cutoff Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(cutoffs).map(([examId, data]) => (
            <Card key={examId} className={`cursor-pointer hover:shadow-lg transition-all border-2 ${selectedExam === examId ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-200 hover:border-emerald-300'}`}
              onClick={() => loadComparison(examId)} data-testid={`cutoff-${examId}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-900 text-sm">{data.name}</h3>
                  <Badge variant="outline" className="text-xs">{data.max_marks}M</Badge>
                </div>
                <div className="space-y-1.5">
                  {Object.entries(data.cutoffs).slice(0, 4).map(([cat, score]) => (
                    <div key={cat} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{CATEGORY_LABELS[cat] || cat}</span>
                      <span className="font-mono font-bold text-slate-800">{score}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-emerald-600 font-medium">View Details</span>
                  <ChevronRight className="w-4 h-4 text-emerald-600" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Comparison Detail */}
        {selectedExam && (
          <div className="space-y-4">
            {compLoading ? (
              <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" /></CardContent></Card>
            ) : comparison && (
              <>
                <Card className="border-emerald-200">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-emerald-600" />
                      {comparison.exam} — Cutoff Comparison
                    </h3>

                    {/* Overall cutoffs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                      {Object.entries(comparison.cutoffs).map(([cat, score]) => {
                        const userScore = comparison.best_score?.total_marks;
                        const maxM = comparison.max_marks;
                        const above = userScore && userScore >= score;
                        return (
                          <div key={cat} className={`p-3 rounded-xl border ${above ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                            <p className="text-xs text-slate-500 uppercase">{CATEGORY_LABELS[cat] || cat}</p>
                            <p className="text-xl font-bold text-slate-900">{score}<span className="text-sm text-slate-400">/{maxM}</span></p>
                            {userScore != null && (
                              <div className="mt-1 flex items-center gap-1">
                                {above ? <CheckCircle className="w-3 h-3 text-green-600" /> : <AlertTriangle className="w-3 h-3 text-amber-600" />}
                                <span className={`text-xs ${above ? 'text-green-600' : 'text-amber-600'}`}>
                                  {above ? 'Clearing' : `Need ${(score - userScore).toFixed(1)} more`}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* User's best */}
                    {comparison.best_score ? (
                      <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                        <p className="text-sm text-emerald-700 font-medium">Your Best Score</p>
                        <p className="text-3xl font-bold text-emerald-800">{comparison.best_score.total_marks}<span className="text-lg text-emerald-500">/{comparison.max_marks}</span></p>
                        <p className="text-xs text-emerald-600">{comparison.best_score.percentage}%</p>
                      </div>
                    ) : (
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <p className="text-sm text-slate-500">No mock test attempts yet for this exam</p>
                        <Button size="sm" className="mt-2" onClick={() => navigate("/banking/mock-test")}>Take a Mock Test</Button>
                      </div>
                    )}

                    {/* Recent scores */}
                    {comparison.recent_scores?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Recent Attempts</p>
                        <div className="space-y-2">
                          {comparison.recent_scores.map((s, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-100">
                              <span className="text-xs text-slate-500">{s.completed_at?.split('T')[0]}</span>
                              <span className="font-mono font-bold text-sm">{s.total_marks} ({s.percentage}%)</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Sectional Cutoffs */}
                {comparison.sectional_cutoffs && Object.keys(comparison.sectional_cutoffs).length > 0 && (
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-slate-900 mb-4">Sectional Cutoffs</h3>
                      <div className="space-y-3">
                        {Object.entries(comparison.sectional_cutoffs).map(([secId, sec]) => (
                          <div key={secId} className="p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-slate-800 capitalize text-sm">{secId.replace(/_/g, ' ')}</span>
                              <span className="text-xs text-slate-500">Max: {sec.max}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-slate-600">General: <strong>{sec.general}</strong></span>
                              <span className="text-slate-600">SC/ST: <strong>{sec.sc_st}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BankingCutoffTrackerPage;
